/**
 * "Provide missing files": the files and folders a compose file bind-mounts that the project lacks on purpose (a key
 * kept out of git: `../operations-lab.json:/app/operations-lab.json:ro`), each saved as a file secret of the service
 * that stands for that path (`provides`, vfs-service src/appconfig/fileSecrets.js). The runner then mounts the secret
 * in its place, read-only, without editing the compose file (preview-runner `driver_trait::compose_provided`), and
 * Publish mounts it at the same container path. Nothing is written into the project: no key in the AI's file tools,
 * in backups or in git.
 *
 * One row per missing source (`missing` of the runnable, vfs-service src/preview/runnables.js):
 * - a file: its secret's name (suggested from the file name), and the file (drop, choose or paste), or Generate for a
 *   key-like file (`generate`: random bytes, base64);
 * - a folder: one or more files that go in it (`provides` ending in `/`), each a file secret of its own;
 * - or Skip (a "Worth a look" folder Docker may well mount empty).
 */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'antd';
import { setFileSecret } from '../../services/AppConfigService';
import { FILE_SECRET_MAX_BYTES, fileToBase64, formatBytes, nameProblem } from '../appconfig/appConfigModel';
import '../appconfig/AppConfig.css';

const shortService = (service) => String(service || '').replace(/^services\//, '');

/** `cookie-secret.key` → `COOKIE_SECRET_KEY`. */
export const secretNameFor = (fileName) => {
  const name = String(fileName || '').toUpperCase().replace(/[^A-Z0-9]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
  return /^[A-Z]/.test(name) ? name : `FILE_${name}`;
};

/** `bytes` random bytes, base64 (a key for a cookie or a token's encryption). */
export function randomKey(bytes = 64) {
  const buffer = new Uint8Array(bytes);
  globalThis.crypto.getRandomValues(buffer);
  let binary = '';
  buffer.forEach((byte) => { binary += String.fromCharCode(byte); });
  return btoa(binary);
}

const emptyFile = () => ({ fileName: '', picked: null, text: '' });

/** The rows' starting state: a file for what is most likely one file, else Skip. */
export function initialRows(missing) {
  return (missing || []).map((item) => ({
    item,
    mode: item.asFile ? 'file' : 'skip',
    name: item.suggestedName || secretNameFor(item.name),
    file: emptyFile(),
    generated: false,
    folder: [emptyFile()],
  }));
}

/** What a row saves: `[{ name, file }]` for setFileSecret, or `{ error }`. */
export function rowSecrets(row) {
  if (row.mode === 'skip') { return []; }
  const contentOf = (entry) => (entry.picked ? { content: entry.picked.base64, encoding: 'base64' } : entry.text ? { content: entry.text, encoding: 'utf8' } : null);
  if (row.mode === 'file') {
    const content = contentOf(row.file);
    if (!content) { return { error: `${row.item.name}: drop or choose the file, paste its content${row.item.generate ? ', or generate it' : ''}, or skip it.` }; }
    const why = nameProblem(row.name.trim());
    if (why) { return { error: `${row.item.name}: ${why}` }; }
    return [{ name: row.name.trim(), file: { ...content, fileName: row.item.name, provides: row.item.source } }];
  }
  const files = row.folder.filter((entry) => entry.fileName.trim() || contentOf(entry));
  if (!files.length) { return { error: `${row.item.name}: add at least one file of the folder, or skip it.` }; }
  const out = [];
  for (const entry of files) {
    const content = contentOf(entry);
    const fileName = entry.fileName.trim() || entry.picked?.fileName || '';
    if (!fileName || !content) { return { error: `${row.item.name}: each file needs a name and its content.` }; }
    out.push({ name: secretNameFor(`${row.name}_${fileName}`), file: { ...content, fileName, provides: `${row.item.source}/` } });
  }
  return out;
}

function FilePick({ entry, onChange, label, testId }) {
  const [problem, setProblem] = useState(null);
  const take = async(file) => {
    if (!file) { return; }
    if (file.size > FILE_SECRET_MAX_BYTES) { setProblem(`${file.name} is ${formatBytes(file.size)}: at most ${formatBytes(FILE_SECRET_MAX_BYTES)}.`); return; }
    try {
      const base64 = await fileToBase64(file);
      setProblem(null);
      onChange({ ...entry, picked: { fileName: file.name, size: file.size, base64 }, text: '', fileName: entry.fileName || file.name });
    } catch (error) {
      setProblem(error.message || 'The file could not be read.');
    }
  };
  return (
    <div className="apps-provide-pick">
      <label className="cfg-drop" data-testid={testId}>
        <input aria-label={`Choose ${label}`} className="cfg-sr" onChange={(e) => { take(e.target.files?.[0]); e.target.value = ''; }} type="file" />
        {entry.picked
          ? <span><strong className="cfg-mono">{entry.picked.fileName}</strong> <span className="cfg-muted">{formatBytes(entry.picked.size)}, ready to save</span></span>
          : <span>Drop or <span className="cfg-linkbtn">choose</span> the file</span>}
      </label>
      {!entry.picked && (
        <textarea
          aria-label={`Or paste ${label}`}
          autoComplete="off"
          className="cfg-input cfg-mono cfg-paste"
          onChange={(e) => onChange({ ...entry, text: e.target.value })}
          placeholder="Or paste its content"
          rows={2}
          spellCheck={false}
          value={entry.text}
        />
      )}
      {problem && <p className="cfg-error" role="alert">{problem}</p>}
    </div>
  );
}

FilePick.propTypes = { entry: PropTypes.object.isRequired, label: PropTypes.string.isRequired, onChange: PropTypes.func.isRequired, testId: PropTypes.string };

function Row({ row, onChange }) {
  const { item } = row;
  const set = (change) => onChange({ ...row, ...change });
  const generate = () => set({ file: { ...emptyFile(), text: randomKey(item.generate?.bytes || 64) }, generated: true });
  return (
    <li className="apps-provide-row" data-testid={`apps-provide-row-${item.source}`}>
      <div className="apps-provide-head">
        <code>{item.path}</code>
        <span className="cfg-muted"> → <code>{item.target}</code> in {item.service}</span>
        {item.blocking && <span className="apps-tag is-bad">Stops the start</span>}
      </div>
      <div className="apps-provide-modes" role="radiogroup" aria-label={`How to provide ${item.name}`}>
        {[['file', 'A secret file'], ['folder', 'A folder of secret files'], ['skip', 'Skip']].map(([mode, label]) => (
          <label key={mode}>
            <input checked={row.mode === mode} name={`mode-${item.source}`} onChange={() => set({ mode })} type="radio" />
            {' '}{label}
          </label>
        ))}
      </div>
      {row.mode === 'file' && (
        <>
          <label className="cfg-field">
            <span>Secret name</span>
            <input aria-label={`Secret name for ${item.name}`} autoComplete="off" className="cfg-input cfg-mono" onChange={(e) => set({ name: e.target.value.toUpperCase() })} value={row.name} />
          </label>
          {row.generated && !row.file.picked
            ? (
              <p className="cfg-muted cfg-small" data-testid={`apps-provide-generated-${item.source}`}>
                Generated: {item.generate?.bytes || 64} random bytes, base64. It is saved as a secret and never shown again.
                {' '}<button className="cfg-linkbtn" onClick={() => set({ file: emptyFile(), generated: false })} type="button">Use my own file instead</button>
              </p>
            )
            : <FilePick entry={row.file} label={item.name} onChange={(file) => set({ file, generated: false })} testId={`apps-provide-drop-${item.source}`} />}
          {item.generate && !row.generated && (
            <button className="cfg-btn is-sm" data-testid={`apps-provide-generate-${item.source}`} onClick={generate} type="button">Generate a random key</button>
          )}
        </>
      )}
      {row.mode === 'folder' && (
        <>
          <p className="cfg-muted cfg-small">Each file goes in the folder by its name, as a secret file of its own.</p>
          {row.folder.map((entry, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <div className="apps-provide-folder-file" key={index}>
              <input
                aria-label={`File name in ${item.name}`}
                autoComplete="off"
                className="cfg-input cfg-mono"
                onChange={(e) => set({ folder: row.folder.map((other, at) => (at === index ? { ...other, fileName: e.target.value } : other)) })}
                placeholder="file name, e.g. openbao_token"
                value={entry.fileName}
              />
              <FilePick entry={entry} label={`${item.name} file ${index + 1}`} onChange={(next) => set({ folder: row.folder.map((other, at) => (at === index ? next : other)) })} />
            </div>
          ))}
          <button className="cfg-btn is-sm is-quiet" onClick={() => set({ folder: [...row.folder, emptyFile()] })} type="button">Add another file</button>
        </>
      )}
    </li>
  );
}

Row.propTypes = { onChange: PropTypes.func.isRequired, row: PropTypes.object.isRequired };

export default function ProvideMissingFiles({ open, projectId, service, missing, onClose, onSaved = null }) {
  const [rows, setRows] = useState(() => initialRows(missing));
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const [seen, setSeen] = useState(missing);
  if (seen !== missing) {
    setSeen(missing);
    setRows(initialRows(missing));
  }
  const save = async() => {
    const all = [];
    for (const row of rows) {
      const out = rowSecrets(row);
      if (out.error) { setProblem(out.error); return; }
      all.push(...out);
    }
    if (!all.length) { setProblem('Nothing to save: every row is skipped.'); return; }
    setBusy(true);
    setProblem(null);
    try {
      for (const secret of all) {
        // eslint-disable-next-line no-await-in-loop
        await setFileSecret(projectId, shortService(service), secret.name, secret.file);
      }
      onSaved?.(all.map((secret) => secret.name));
      onClose();
    } catch (error) {
      setProblem(error?.message || 'The secret files could not be saved.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      cancelText="Cancel"
      confirmLoading={busy}
      okButtonProps={{ 'data-testid': 'apps-provide-save' }}
      okText="Save as secret files"
      onCancel={onClose}
      onOk={save}
      open={open}
      title="Provide the missing files"
      width={680}
    >
      <p className="cfg-muted cfg-small">
        Saved as secret files of <code>{shortService(service)}</code>: encrypted, never in the project, in git or in the AI&apos;s files.
        Flow mounts each one where the compose file expects it, read-only, without changing the compose file; Publish mounts it at the same path.
      </p>
      <ul className="apps-provide-list">
        {rows.map((row, index) => <Row key={row.item.source} onChange={(next) => setRows(rows.map((other, at) => (at === index ? next : other)))} row={row} />)}
      </ul>
      {problem && <p className="cfg-error" role="alert">{problem}</p>}
    </Modal>
  );
}

ProvideMissingFiles.propTypes = {
  // `missing` of the runnable: [{ name, source, path, service, target, file, blocking, asFile, suggestedName, generate }]
  missing: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  // (names) after saving: reload the runnables, so the problems go.
  onSaved: PropTypes.func,
  open: PropTypes.bool.isRequired,
  projectId: PropTypes.string.isRequired,
  // The service folder (`services/adk-be-app`).
  service: PropTypes.string.isRequired,
};
